import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { ErrorList } from '@/shared/ui/ErrorList';
import { FilterDateTime } from '@/shared/ui/FilterDateTime';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { TextField } from '@/shared/ui/TextField';
import type { SelectOption } from '@/shared/ui/selectTypes';
import { deleteBidFile, editBid, fetchBid, type BidDetail } from '../detailApi';
import { bidKey } from './useBidAction';

const REQUEST_TYPE_OPTIONS: SelectOption[] = [
  { value: 'client', label: 'Клиентская' },
  { value: 'test', label: 'Тестовая' },
  { value: 'duplicate', label: 'Дубль' },
];

/** Statuses where the operator may approve (→0) or reject (→4) the bid from the editor. */
const EDITABLE_STATUS = new Set([-1, 0, 4]);

const statusLabel = (statusId: number) => {
  if (statusId === -1) return 'Ожидает модерации';
  if (statusId === 0) return 'Новая модерированная';
  if (statusId === 4) return 'Отклонена';
  return 'Статус не определен';
};

type FormValues = {
  description: string;
  requestType: string;
  needEvacuator: boolean;
  ownSpareParts: boolean;
  desiredRepairTime: Date | null;
  desiredCost: string;
  statusId: number;
};

type ImageRow = {
  id?: number;
  url?: string;
  thumb_140_140?: string;
  thumb_120_120?: string;
};

const toValues = (bid: BidDetail): FormValues => ({
  description: bid.description ?? '',
  requestType: bid.request_type?.code ?? 'client',
  needEvacuator: Boolean(Number(bid.need_evacuator)),
  ownSpareParts: Boolean(Number(bid.own_spare_parts)),
  desiredRepairTime: bid.desired_repair_time
    ? new Date(Number(bid.desired_repair_time) * 1000)
    : null,
  desiredCost: bid.desired_cost != null && bid.desired_cost !== '' ? String(bid.desired_cost) : '',
  statusId: Number(bid.status_id),
});

type FormProps = {
  bid: BidDetail;
  onClose: () => void;
};

const BidEditForm = ({ bid, onClose }: FormProps) => {
  const qc = useQueryClient();
  const { values, setField, errors, save, isSaving } = useEntityForm(
    toValues(bid),
    (v: FormValues) =>
      editBid({
        bid_id: bid.id,
        description: v.description,
        need_evacuator: v.needEvacuator ? 1 : 0,
        own_spare_parts: v.ownSpareParts ? 1 : 0,
        desired_repair_time: v.desiredRepairTime
          ? Math.floor(v.desiredRepairTime.getTime() / 1000)
          : undefined,
        desired_cost: v.desiredCost === '' ? undefined : v.desiredCost,
        status_id: v.statusId,
        request_type: v.requestType,
      }),
    () => {
      void qc.invalidateQueries({ queryKey: ['bids'] });
      void qc.invalidateQueries({ queryKey: bidKey(bid.id) });
      onClose();
    },
  );

  const removeFile = useMutation({
    mutationFn: (fileId: number) => deleteBidFile(fileId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: bidKey(bid.id) });
      void qc.invalidateQueries({ queryKey: ['bids'] });
    },
  });

  const images = (bid.images ?? []) as ImageRow[];
  const canModerate = EDITABLE_STATUS.has(Number(bid.status_id));
  const rejected = values.statusId === 4;

  return (
    <ModalDialog
      title={`Редактирование заявки #${bid.id}`}
      onClose={onClose}
      onSave={save}
      isSaving={isSaving}
    >
      <TextField
        label="Описание заявки"
        multiline
        value={values.description}
        onChange={(v) => setField('description', v)}
        errors={errors.description}
      />
      <div className="form-group">
        <label className="control-label">Тип заявки</label>
        <FilterSelect
          options={REQUEST_TYPE_OPTIONS}
          value={values.requestType}
          width="100%"
          isClearable={false}
          onChange={(v) => setField('requestType', v, 'request_type')}
        />
      </div>
      <div className="checkbox">
        <label>
          <input
            type="checkbox"
            checked={values.needEvacuator}
            onChange={(e) => setField('needEvacuator', e.target.checked, 'need_evacuator')}
          />{' '}
          Нужен эвакуатор
        </label>
      </div>
      <div className="checkbox">
        <label>
          <input
            type="checkbox"
            checked={values.ownSpareParts}
            onChange={(e) => setField('ownSpareParts', e.target.checked, 'own_spare_parts')}
          />{' '}
          Свои запчасти
        </label>
      </div>
      <div className="form-group">
        <label className="control-label">Желаемая дата ремонта</label>
        <FilterDateTime
          value={values.desiredRepairTime}
          onChange={(v) => setField('desiredRepairTime', v, 'desired_repair_time')}
          placeholder="Дата не указана"
        />
      </div>
      <TextField
        label="Желаемая сумма"
        value={values.desiredCost}
        onChange={(v) => {
          if (v === '' || /^\d*\.?\d*$/.test(v)) setField('desiredCost', v, 'desired_cost');
        }}
        errors={errors.desired_cost}
      />
      {canModerate && (
        <div className={`info-box ${rejected ? 'bg-red' : 'bg-green'}`} style={{ marginTop: 12 }}>
          <span className="info-box-icon">
            <i className={`fa ${rejected ? 'fa-thumbs-o-down' : 'fa-thumbs-o-up'}`} />
          </span>
          <div className="info-box-content">
            <span className="info-box-text">
              <em>{statusLabel(values.statusId)}</em>
            </span>
            <div className="progress">
              <div className="progress-bar" style={{ width: rejected ? '0%' : '100%' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" className="btn btn-success btn-sm" onClick={() => setField('statusId', 0, 'status_id')}>
                Подтвердить
              </button>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => setField('statusId', 4, 'status_id')}>
                Отклонить
              </button>
            </div>
          </div>
        </div>
      )}
      {images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {images.map((image) => {
            const src = image.thumb_140_140 || image.thumb_120_120 || image.url;
            return (
              <div key={String(image.id ?? src)} style={{ position: 'relative' }}>
                <a href={image.url || src} target="_blank" rel="noopener noreferrer">
                  <img src={src} alt="" style={{ height: 100, objectFit: 'cover' }} />
                </a>
                {image.id != null && (
                  <button
                    type="button"
                    className="btn btn-danger btn-xs"
                    style={{ position: 'absolute', top: 2, right: 2 }}
                    disabled={removeFile.isPending}
                    onClick={() => removeFile.mutate(Number(image.id))}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <ErrorList errors={errors} />
    </ModalDialog>
  );
};

type Props = {
  bidId: number;
  onClose: () => void;
};

/** Pencil-button editor for a bid (legacy BidEditor without image cropper upload). */
export const BidEditModal = ({ bidId, onClose }: Props) => {
  const { data: bid, isLoading, error } = useQuery({
    queryKey: bidKey(bidId, 'view'),
    queryFn: () => fetchBid(bidId),
  });

  if (isLoading || error || !bid) {
    return (
      <ModalDialog title={`Редактирование заявки #${bidId}`} onClose={onClose}>
        <QueryStatus isLoading={isLoading} error={error} errorText="Не удалось загрузить заявку" />
      </ModalDialog>
    );
  }

  return <BidEditForm key={bid.id} bid={bid} onClose={onClose} />;
};
